import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  Tooltip,
  Snackbar,
  Alert,
} from "@mui/material";
import { useThemeContext } from "../../../Context/ThemeContext";
import { DARKCOLORS, LIGHTCOLORS, ROUTES } from "../../../Utils/constants";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import ExportIcon from "../../../assets/Export_icon.svg";
import "./VendingMachines.css";

const CustomEditIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="11"
    height="12"
    viewBox="0 0 11 12"
    fill="none"
  >
    <path
      d="M9.14375 0.51752C8.97972 0.353403 8.78496 0.223223 8.57059 0.134424C8.35622 0.0456243 8.12645 -5.41271e-05 7.89442 4.8133e-08C7.66239 5.42234e-05 7.43264 0.0458399 7.21831 0.134739C7.00399 0.223639 6.80928 0.353909 6.64533 0.518103L1.09958 6.07144C0.769145 6.40264 0.583498 6.85134 0.583333 7.31919V9.1876C0.583333 9.4291 0.779333 9.6251 1.02083 9.6251H2.90092C3.36933 9.6251 3.8185 9.43844 4.14925 9.10769L9.69092 3.56252C10.0215 3.23117 10.2071 2.78223 10.2071 2.31419C10.2071 1.84614 10.0215 1.3972 9.69092 1.06585L9.14375 0.51752ZM0.4375 10.5001C0.321468 10.5001 0.210188 10.5462 0.128141 10.6282C0.0460936 10.7103 0 10.8216 0 10.9376C0 11.0536 0.0460936 11.1649 0.128141 11.247C0.210188 11.329 0.321468 11.3751 0.4375 11.3751H9.77083C9.88687 11.3751 9.99815 11.329 10.0802 11.247C10.1622 11.1649 10.2083 11.0536 10.2083 10.9376C10.2083 10.8216 10.1622 10.7103 10.0802 10.6282C9.99815 10.5462 9.88687 10.5001 9.77083 10.5001H0.4375Z"
      fill="#9586EA"
    />
  </svg>
);

// Translation keys for the table only (Excel export uses raw values).
// Replace these with the same keys your filter dropdown uses.
const STATUS_KEYS = {
  Active: "vendingMachines.active",
  Inactive: "vendingMachines.inactive",
};

const CONDITION_KEYS = {
  Normal: "vendingMachines.normal",
  Abnormal: "vendingMachines.abnormal",
};

const MachineTable = ({ machines }) => {
  const { t } = useTranslation();
  const { mode } = useThemeContext();
  const navigate = useNavigate();

  // Table only. Excel export does not use these.
  const getStatusLabel = (status) => {
    if (!status) return t("sales_analytics.NoData");
    const key = STATUS_KEYS[status];
    return key ? t(key, { defaultValue: status }) : status;
  };

  const getConditionLabel = (condition) => {
    if (condition == null || condition === "")
      return t("sales_analytics.NoData");
    const key =
      condition === "Normal" ? CONDITION_KEYS.Normal : CONDITION_KEYS.Abnormal;
    return t(key, {
      defaultValue: condition === "Normal" ? "Normal" : "Abnormal",
    });
  };

  const containerBackground =
    mode === "dark" ? DARKCOLORS.BackgroundMenu : LIGHTCOLORS.BackgroundCard;
  const textColor =
    mode === "dark"
      ? DARKCOLORS.HeadingLightBackground
      : LIGHTCOLORS.HeadingLightBackground;
  const textColor2 =
    mode === "dark"
      ? LIGHTCOLORS.HeadingLightBackground
      : DARKCOLORS.HeadingLightBackground;
  const bodyColor =
    mode === "dark"
      ? DARKCOLORS.BodyLightBackground
      : LIGHTCOLORS.BodyLightBackground;

  const colorSuccessExport =
    mode === "dark" ? DARKCOLORS.Secondary500 : LIGHTCOLORS.Secondary500;
  const settingIconColor =
    mode === "dark" ? DARKCOLORS.BaseBlack : LIGHTCOLORS.BaseBlack;

  const colorDanger =
    mode === "dark" ? DARKCOLORS.Danger500 : LIGHTCOLORS.Danger500;
  const colorDanger1 =
    mode === "dark" ? DARKCOLORS.Danger100 : LIGHTCOLORS.Danger100;
  const colorDanger3 =
    mode === "dark" ? DARKCOLORS.Danger600 : LIGHTCOLORS.Danger600;

  const colorWarning =
    mode === "dark" ? DARKCOLORS.Warning500 : LIGHTCOLORS.Warning500;
  const colorWarning1 =
    mode === "dark" ? DARKCOLORS.Warning100 : LIGHTCOLORS.Warning100;
  const colorWarning3 =
    mode === "dark" ? DARKCOLORS.Warning300 : LIGHTCOLORS.Warning300;
  const colorWarning2 =
    mode === "dark" ? DARKCOLORS.Warning700 : LIGHTCOLORS.Warning700;

  const colorSuccess =
    mode === "dark" ? DARKCOLORS.Success700 : LIGHTCOLORS.Success700;
  const colorSuccess1 =
    mode === "dark" ? DARKCOLORS.Success100 : LIGHTCOLORS.Success100;
  const colorSuccess3 =
    mode === "dark" ? DARKCOLORS.Success500 : LIGHTCOLORS.Success500;

  const borderColor =
    mode === "dark" ? DARKCOLORS.GreyScale20 : LIGHTCOLORS.GreyScale20;
  const backgroundColor1 =
    mode === "dark" ? DARKCOLORS.GreyScale10 : LIGHTCOLORS.GreyScale10;
  const scroll1 =
    mode === "light"
      ? LIGHTCOLORS.BodyDarkBackground
      : DARKCOLORS.BodyLightBackground;
  const scroll =
    mode === "light" ? LIGHTCOLORS.GreyScale40 : DARKCOLORS.GreyScale40;
  const badgecolor = DARKCOLORS.BodyDarkBackground;

  // Greyscale badge for missing (null) backend data
  const greyBadgeBg =
    mode === "dark" ? DARKCOLORS.GreyScale10 : LIGHTCOLORS.GreyScale10;
  const greyBadgeBorder =
    mode === "dark" ? DARKCOLORS.GreyScale40 : LIGHTCOLORS.GreyScale40;
  const greyBadgeText =
    mode === "dark"
      ? DARKCOLORS.HeadingLightBackground
      : LIGHTCOLORS.HeadingLightBackground;

  const [open1, setOpen1] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setOpen1(false);
  };

  const showToast = (message) => {
    setToastMessage(message);
    setOpen1(true);
  };

  const handleExport = (machine) => {
    // Build the row first, so we can check whether there is anything to export
    // Only the column headers are translated. Data values stay raw.
    const row = {
      [t("vendingMachines.machineId", { defaultValue: "Machine ID" })]:
        machine?.machineNumber || "-",
      [t("vendingMachines.location", { defaultValue: "Location" })]:
        machine?.location || "-",
      [t("vendingMachines.address", { defaultValue: "Address" })]:
        machine?.street || "-",
      [t("vendingMachines.machine", { defaultValue: "Machine" })]:
        machine?.vendingMachine?.machineType || "-",
      [t("vendingMachines.status", { defaultValue: "Status" })]:
        machine?.status || "-",
      [t("vendingMachines.condition", { defaultValue: "Condition" })]:
        machine?.condition == null || machine?.condition === ""
          ? "-"
          : machine.condition === "Normal"
            ? "Normal"
            : "Abnormal",
      [t("vendingMachines.temperature", { defaultValue: "Temperature" })]:
        machine?.temperature !== null && machine?.temperature !== undefined
          ? `${machine.temperature}°C`
          : "-",
      [t("vendingMachines.level", { defaultValue: "Level" })]:
        machine?.inventoryLevel !== null &&
        machine?.inventoryLevel !== undefined
          ? `${Number(machine.inventoryLevel).toFixed(1)}%`
          : "-",
      [t("vendingMachines.version", { defaultValue: "Version" })]:
        machine?.version ? `V ${machine.version}` : "-",
    };

    // No data: every column is "-"
    const hasData = Object.values(row).some((v) => v !== "-");

    if (!machine || !hasData) {
      showToast(t("sales_analytics.noDataToExport", "No data to export"));
      return;
    }

    try {
      const ws = XLSX.utils.json_to_sheet([row]);

      ws["!cols"] = [
        { wch: 18 },
        { wch: 25 },
        { wch: 35 },
        { wch: 25 },
        { wch: 15 },
        { wch: 15 },
        { wch: 15 },
        { wch: 15 },
        { wch: 15 },
      ];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Machine Details");

      const ts = new Date().toISOString().replace(/[-:T]/g, "").slice(0, 14);
      const fileName = `${t(
        "vendingMachines.export_file_name",
        "Machine_Details",
      )}_${ts}.xlsx`;

      XLSX.writeFile(wb, fileName);
    } catch (error) {
      console.error("Export failed:", error);
      showToast(
        t("vendingMachines.errorExportingData", "Error exporting data"),
      );
    }
  };

  // Status badge styles
  const getStatusBadgeStyles = (status) => {
    if (!status) {
      return {
        backgroundColor: greyBadgeBg,
        border: `1px solid ${greyBadgeBorder}`,
        textstyle: greyBadgeText,
      };
    }
    switch (status) {
      case "Active":
        return {
          backgroundColor: colorSuccess1,
          border: `1px solid ${colorSuccess3}`,
          textstyle: colorSuccess,
        };
      case "Inactive":
        return {
          backgroundColor: colorDanger1,
          border: `1px solid ${colorDanger}`,
          textstyle: colorDanger3,
        };
      default:
        return {
          backgroundColor: colorWarning1,
          border: `1px solid ${colorWarning3}`,
          textstyle: colorWarning2,
        };
    }
  };

  // Condition badge styles
  const getConditionBadgeStyles = (condition) => {
    if (!condition) {
      return {
        backgroundColor: greyBadgeBg,
        border: `1px solid ${greyBadgeBorder}`,
        color: greyBadgeText,
        textstyle: greyBadgeText,
      };
    }
    const isNormal = condition === "Normal";
    return {
      backgroundColor: isNormal ? colorSuccess1 : colorDanger1,
      border: `1px solid ${isNormal ? colorSuccess3 : colorDanger}`,
      color: isNormal ? colorSuccess3 : colorDanger,
      textstyle: isNormal ? colorSuccess : colorDanger3,
    };
  };

  // Inventory chip styles
  const getInventoryChipStyles = (level, machineType) => {
    // ponytail: defensive null — don't show badges when level data is missing or non-numeric
    if (level == null || typeof level !== "number") return null;
    const type = (machineType || "").toLowerCase();
    const isReturnMachine =
      type.includes("return") ||
      type.includes("recycle") ||
      type.includes("greeny");

    if (isReturnMachine) {
      // Return type machines: Almost Full (70-90%), Full (>=90%)
      if (level >= 90) {
        return {
          label: t("dashboard.full"),
          backgroundColor: colorDanger,
          border: `1px solid ${colorDanger}`,
          color: textColor2,
        };
      } else if (level >= 70 && level < 90) {
        return {
          label: t("dashboard.almostFull") || "Almost Full",
          backgroundColor: colorWarning,
          border: `1px solid ${colorWarning}`,
          color: textColor2,
        };
      }
      return null;
    }

    // Regular machines: Low (<30%), Restock (30-70%) — no Full badge
    if (level >= 30 && level < 70) {
      return {
        label: t("dashboard.restock"),
        backgroundColor: colorWarning,
        border: `1px solid ${colorWarning}`,
        color: textColor2,
      };
    } else if (level < 30) {
      return {
        label: t("dashboard.low"),
        backgroundColor: colorDanger,
        border: `1px solid ${colorDanger}`,
        color: textColor2,
      };
    }
    return null;
  };

  const handleEditClick = (machineId) => {
    navigate(`${ROUTES.MACHINE_DETAILS}/${machineId}`);
  };

  return (
    <Box sx={{ marginBottom: "50px", marginTop: "25px" }}>
      <TableContainer
        className="machine-table-container"
        component={Paper}
        sx={{
          backgroundColor: containerBackground,
          boxShadow: "none",
          borderRadius: "12px",
          overflowX: "Scroll",
          border: `1px solid ${borderColor}`,
          padding: "0px 10px ",
          paddingBottom: " 10px ",
          minHeight: "65vh",
          width: "100%",
          // maxWidth: 'auto',
          maxWidth: "100%",
          "&::-webkit-scrollbar": {
            width: "10px !important",
            height: "8px !important",
            padding: "2px !important",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: scroll1,
            borderRadius: "10px",
            border: `1px solid ${scroll}`,
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: scroll,
            borderRadius: "10px",
            width: "3px !important",

            border: "3px solid transparent",
            backgroundClip: "padding-box",
            "&:hover": {
              backgroundColor: scroll,
            },
          },
        }}
      >
        <Table sx={{ minWidth: 900 }} aria-label="machine table">
          <TableHead>
            <TableRow>
              <TableCell
                className="Machinetable_Head"
                sx={{
                  color: textColor,
                  fontFamily: "Satoshi-medium",
                  fontWeight: 500,
                  fontSize: "14px",
                }}
              >
                {t("vendingMachines.machineId") || "ID"}
              </TableCell>
              <TableCell
                className="Machinetable_Head"
                sx={{
                  color: textColor,
                  fontFamily: "Satoshi-medium",
                  fontWeight: 500,
                  fontSize: "14px",
                }}
              >
                {t("vendingMachines.location") || "Location"}
              </TableCell>
              <TableCell
                className="Machinetable_Head"
                sx={{
                  color: textColor,
                  fontFamily: "Satoshi-medium",
                  fontWeight: 500,
                  fontSize: "14px",
                }}
              >
                {t("vendingMachines.address") || "Address"}
              </TableCell>
              <TableCell
                className="Machinetable_Head"
                sx={{
                  color: textColor,
                  fontFamily: "Satoshi-medium",
                  fontWeight: 500,
                  fontSize: "14px",
                }}
              >
                {t("vendingMachines.machine") || "Machine"}
              </TableCell>
              <TableCell
                className="Machinetable_Head Condition_table"
                sx={{
                  color: textColor,
                  fontFamily: "Satoshi-medium",
                  fontWeight: 500,
                  fontSize: "14px",
                }}
              >
                {t("vendingMachines.status") || "Status"}
              </TableCell>
              <TableCell
                className="Machinetable_Head  table_condition "
                sx={{
                  color: textColor,
                  fontFamily: "Satoshi-medium",
                  fontWeight: 500,
                  fontSize: "14px",
                }}
              >
                {t("vendingMachines.condition") || "Condition"}
              </TableCell>
              <TableCell
                className="Machinetable_Head Condition_table"
                align="center"
                sx={{
                  color: textColor,
                  fontFamily: "Satoshi-medium",
                  fontWeight: 500,
                  fontSize: "14px",
                }}
              >
                {t("vendingMachines.temperature") || "Temperature"}
              </TableCell>
              <TableCell
                className="Machinetable_Head  tabel-cell-space"
                align="center"
                sx={{
                  color: textColor,
                  fontFamily: "Satoshi-medium",
                  fontWeight: 500,
                  fontSize: "14px",
                }}
              >
                {t("vendingMachines.level") || "Level"}
              </TableCell>
              <TableCell
                className="Machinetable_Head Condition_table"
                align="center"
                sx={{
                  color: textColor,
                  fontFamily: "Satoshi-medium",
                  fontWeight: 500,
                  fontSize: "14px",
                }}
              >
                {t("vendingMachines.version") || "Version"}
              </TableCell>
              <TableCell
                sx={{
                  color: textColor,
                  fontFamily: "Satoshi-medium",
                  fontWeight: 500,
                  fontSize: "14px",
                  width: "60px",
                }}
              >
                {" "}
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody sx={{}}>
            {machines.map((machine) => {
              const statusStyles = getStatusBadgeStyles(machine.status);
              const conditionStyles = getConditionBadgeStyles(
                machine.condition,
              );
              const inventoryChip = machine.inventoryLevel
                ? getInventoryChipStyles(
                    machine.inventoryLevel,
                    machine?.vendingMachine?.machineType,
                  )
                : null;
              // Table only: translated. Excel export keeps the raw machine type.
              const rawMachineType = machine?.vendingMachine?.machineType;
              const machineTypeLabel = rawMachineType
                ? t(`vendingMachines.${rawMachineType.split(" ")[0]}`, {
                    defaultValue: rawMachineType,
                  })
                : "-";
              return (
                <TableRow
                  key={machine.id}
                  sx={{
                    "&:last-child td, &:last-child th": { border: 0 },
                    "&:nth-of-type(even)": {
                      background: backgroundColor1,
                    },
                  }}
                >
                  <TableCell
                    className="Machinetable_cell"
                    sx={{
                      color: bodyColor,
                      fontFamily: "Satoshi",
                      fontSize: "13px",
                      borderBottom: `none`,
                      borderRadius: "10px 0px 0px 10px",
                      padding: "0px 10px",
                    }}
                  >
                    {machine.machineNumber || t("sales_analytics.NoData")}
                  </TableCell>
                  <TableCell
                    className="Machinetable_cell"
                    sx={{
                      color: bodyColor,
                      fontFamily: "Satoshi-medium",
                      fontSize: "14px",
                      borderBottom: `none`,
                      padding: "0px 10px",
                    }}
                  >
                    <Tooltip
                      title={machine.location || "Unnamed Machine"}
                      arrow
                    >
                      {machine.location || t("sales_analytics.NoData")}
                    </Tooltip>
                  </TableCell>
                  <TableCell
                    className="Machinetable_cell"
                    sx={{
                      color: bodyColor,
                      fontFamily: "Satoshi",
                      fontSize: "13px",
                      borderBottom: `none`,
                      padding: "0px 10px",
                    }}
                  >
                    <Tooltip title={machine.street || "Unknown Location"} arrow>
                      {machine.street || t("sales_analytics.NoData")}
                    </Tooltip>
                  </TableCell>
                  <TableCell
                    className="Machinetable_cell"
                    sx={{
                      color: bodyColor,
                      fontFamily: "Satoshi",
                      fontSize: "13px",
                      borderBottom: `none`,
                      padding: "0px 10px",
                    }}
                  >
                    <Tooltip title={machineTypeLabel} arrow>
                      {machineTypeLabel}
                    </Tooltip>
                  </TableCell>
                  <TableCell
                    className="Machinetable_cell Condition_table"
                    sx={{ borderBottom: `none`, padding: "0px 10px" }}
                  >
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                        padding: "4px 10px",
                        borderRadius: "99px",
                        backgroundColor: statusStyles.backgroundColor,
                        border: statusStyles.border,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "12px",
                          fontWeight: 500,
                          color: statusStyles.textstyle,
                          fontFamily: "Satoshi-medium",
                        }}
                      >
                        {getStatusLabel(machine.status)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell
                    className="Machinetable_cell  table_condition "
                    sx={{
                      borderBottom: `none`,
                      padding: "0px 10px",
                      width: "1%",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                        padding: "4px 10px",
                        borderRadius: "99px",
                        backgroundColor: conditionStyles.backgroundColor,
                        border: conditionStyles.border,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "13px",
                          fontWeight: 500,
                          color: conditionStyles.textstyle,
                          fontFamily: "Satoshi-medium",
                        }}
                      >
                        {getConditionLabel(machine.condition)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell
                    className="Machinetable_cell Condition_table "
                    align="center"
                    sx={{
                      color: bodyColor,
                      fontFamily: "Satoshi",
                      fontSize: "13px",
                      borderBottom: `none`,
                      padding: "0px 10px",
                      width: "1%",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {machine.temperature !== null
                      ? `${machine.temperature}°C`
                      : "-"}
                  </TableCell>
                  <TableCell
                    className="Machinetable_cell  tabel-cell-space "
                    align="center"
                    sx={{ borderBottom: `none`, padding: "0px 10px" }}
                  >
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "3px",
                        flexWrap: "wrap",
                        minWidth: "80px",
                      }}
                    >
                      <Typography
                        sx={{
                          color: bodyColor,
                          fontFamily: "Satoshi",
                          fontSize: "13px",
                        }}
                      >
                        {machine.inventoryLevel
                          ? `${machine.inventoryLevel.toFixed(1)}%`
                          : "-"}
                      </Typography>
                      {inventoryChip && (
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            padding: "3px 8px",
                            borderRadius: "99px",
                            backgroundColor: inventoryChip.backgroundColor,
                            border: inventoryChip.border,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "8px",
                              fontWeight: 500,
                              color: badgecolor,
                              fontFamily: "Satoshi-medium",
                            }}
                          >
                            {inventoryChip.label}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell
                    className="Machinetable_cell Condition_table"
                    align="center"
                    sx={{
                      color: bodyColor,
                      fontFamily: "Satoshi",
                      fontSize: "13px",
                      borderBottom: `none`,
                      padding: "0px 10px",
                    }}
                  >
                    {machine.version ? `V ${machine.version}` : "-"}
                  </TableCell>
                  <TableCell
                    sx={{
                      display: "flex",
                      borderBottom: `none`,
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "0px 10px 10px 0px",
                      padding: "10px 10px",
                    }}
                  >
                    <Box
                      onClick={() => handleEditClick(machine.id)}
                      sx={{
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "32px",
                        height: "32px",
                        borderRadius: "50%",
                        "&:hover": {
                          backgroundColor:
                            mode === "dark"
                              ? DARKCOLORS.GreyScale20
                              : LIGHTCOLORS.GreyScale20,
                        },
                        transition: "background-color 0.2s ease",
                      }}
                      title={t("vendingMachines.edit") || "Edit machine"}
                    >
                      <CustomEditIcon />
                    </Box>
                    <Box
                      sx={{
                        background: colorSuccessExport,
                        color: settingIconColor,
                        padding: "6px",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "24px",
                        height: "24px",
                        flexShrink: 0,
                        cursor: "pointer",
                        marginLeft: "10px",
                      }}
                      onClick={() => handleExport(machine)}
                    >
                      <img
                        src={ExportIcon}
                        alt="Export"
                        style={{ width: "12px", height: "12px" }}
                      />
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {/* Snackbar */}
      <Snackbar
        open={open1}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="error"
          sx={{ width: "100%" }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MachineTable;
